/**
 * Utility functions for device information display
 */

/**
 * Get friendly browser name from user agent string or browser identifier
 * @param {string} browser - Browser string from device info
 * @param {string} userAgent - Full user agent string (optional)
 * @returns {string|null} Friendly browser name or null if unknown
 */
export const getFriendlyBrowserName = (browser, userAgent = '') => {
  if (!browser && !userAgent) return null;
  
  const browserLower = (browser || userAgent || '').toLowerCase();
  
  // Chrome-based browsers
  if (browserLower.includes('edg/') || browserLower.includes('edge')) {
    return 'Microsoft Edge';
  }
  if (browserLower.includes('opr/') || browserLower.includes('opera')) {
    return 'Opera';
  }
  if (browserLower.includes('brave')) {
    return 'Brave';
  }
  if (browserLower.includes('vivaldi')) {
    return 'Vivaldi';
  }
  if (browserLower.includes('chrome') || browserLower.includes('crios')) {
    return 'Google Chrome';
  }
  
  // Firefox
  if (browserLower.includes('firefox') || browserLower.includes('fxios')) {
    return 'Mozilla Firefox';
  }
  
  // Safari
  if (browserLower.includes('safari') && !browserLower.includes('chrome')) {
    if (browserLower.includes('mobile') || browserLower.includes('iphone') || browserLower.includes('ipad')) {
      return 'Safari (Mobile)';
    }
    return 'Safari';
  }
  
  // Internet Explorer / Legacy Edge
  if (browserLower.includes('trident') || browserLower.includes('msie')) {
    return 'Internet Explorer';
  }
  
  // Mobile browsers
  if (browserLower.includes('instagram')) {
    return 'Instagram Browser';
  }
  if (browserLower.includes('facebook')) {
    return 'Facebook Browser';
  }
  if (browserLower.includes('twitter')) {
    return 'Twitter Browser';
  }
  if (browserLower.includes('linkedin')) {
    return 'LinkedIn Browser';
  }
  if (browserLower.includes('whatsapp')) {
    return 'WhatsApp Browser';
  }
  if (browserLower.includes('telegram')) {
    return 'Telegram Browser';
  }
  
  // Other browsers
  if (browserLower.includes('duckduckgo')) {
    return 'DuckDuckGo Browser';
  }
  if (browserLower.includes('samsung')) {
    return 'Samsung Internet';
  }
  if (browserLower.includes('ucbrowser')) {
    return 'UC Browser';
  }
  if (browserLower.includes('yabrowser')) {
    return 'Yandex Browser';
  }
  
  // Fallback: capitalize first letter of the browser string
  if (browser && browser !== 'unknown') {
    return browser.charAt(0).toUpperCase() + browser.slice(1);
  }
  
  return null;
};

/**
 * Get friendly operating system name
 * @param {string} os - OS string from device info
 * @param {string} userAgent - Full user agent string (optional)
 * @returns {string|null} Friendly OS name or null if unknown
 */
export const getFriendlyOSName = (os, userAgent = '') => {
  if (!os && !userAgent) return null;
  
  const osLower = (os || userAgent || '').toLowerCase();
  
  // Windows
  if (osLower.includes('windows nt 10.0')) {
    return 'Windows 10/11';
  }
  if (osLower.includes('windows nt 6.3')) {
    return 'Windows 8.1';
  }
  if (osLower.includes('windows nt 6.2')) {
    return 'Windows 8';
  }
  if (osLower.includes('windows nt 6.1')) {
    return 'Windows 7';
  }
  if (osLower.includes('windows')) {
    return 'Windows';
  }
  
  // macOS
  if (osLower.includes('mac os x 10_15') || osLower.includes('macos 10.15')) {
    return 'Mac OS Catalina';
  }
  if (osLower.includes('mac os x 11_') || osLower.includes('macos 11.')) {
    return 'Mac OS Big Sur';
  }
  if (osLower.includes('mac os x 12_') || osLower.includes('macos 12.')) {
    return 'Mac OS Monterey';
  }
  if (osLower.includes('mac os x 13_') || osLower.includes('macos 13.')) {
    return 'Mac OS Ventura';
  }
  if (osLower.includes('mac os x 14_') || osLower.includes('macos 14.')) {
    return 'Mac OS Sonoma';
  }
  if (osLower.includes('mac os x') || osLower.includes('macos') || osLower.includes('darwin')) {
    return 'Mac OS';
  }
  
  // iOS
  if (osLower.includes('iphone os') || osLower.includes('ios')) {
    if (osLower.includes('iphone os 17_') || osLower.includes('ios 17.')) {
      return 'iOS 17';
    }
    if (osLower.includes('iphone os 16_') || osLower.includes('ios 16.')) {
      return 'iOS 16';
    }
    if (osLower.includes('iphone os 15_') || osLower.includes('ios 15.')) {
      return 'iOS 15';
    }
    return 'iOS';
  }
  
  // iPadOS
  if (osLower.includes('ipad')) {
    return 'iPadOS';
  }
  
  // Android
  if (osLower.includes('android')) {
    // Try to extract version
    const androidMatch = osLower.match(/android (\d+)/);
    if (androidMatch) {
      const version = parseInt(androidMatch[1]);
      if (version >= 14) return 'Android 14+';
      if (version >= 13) return 'Android 13';
      if (version >= 12) return 'Android 12';
      if (version >= 11) return 'Android 11';
      if (version >= 10) return 'Android 10';
      if (version >= 9) return 'Android 9';
      if (version >= 8) return 'Android 8';
      return `Android ${version}`;
    }
    return 'Android';
  }
  
  // Linux
  if (osLower.includes('ubuntu')) {
    return 'Ubuntu Linux';
  }
  if (osLower.includes('debian')) {
    return 'Debian Linux';
  }
  if (osLower.includes('fedora')) {
    return 'Fedora Linux';
  }
  if (osLower.includes('centos')) {
    return 'CentOS Linux';
  }
  if (osLower.includes('linux')) {
    return 'Linux';
  }
  
  // Other systems
  if (osLower.includes('chromeos') || osLower.includes('cros')) {
    return 'Chrome OS';
  }
  if (osLower.includes('freebsd')) {
    return 'FreeBSD';
  }
  if (osLower.includes('openbsd')) {
    return 'OpenBSD';
  }
  if (osLower.includes('netbsd')) {
    return 'NetBSD';
  }
  
  // Fallback: capitalize first letter of the OS string
  if (os && os !== 'unknown') {
    return os.charAt(0).toUpperCase() + os.slice(1);
  }
  
  return null;
};

/**
 * Get friendly device type name
 * @param {string} deviceType - Device type from device info
 * @param {string} userAgent - Full user agent string (optional)
 * @returns {string|null} Friendly device type name or null if unknown
 */
export const getFriendlyDeviceType = (deviceType, userAgent = '') => {
  if (!deviceType && !userAgent) return null;
  
  const deviceLower = (deviceType || userAgent || '').toLowerCase();
  
  if (deviceLower.includes('mobile') || deviceLower.includes('phone')) {
    return 'Mobile Phone';
  }
  if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) {
    return 'Tablet';
  }
  if (deviceLower.includes('desktop') || deviceLower.includes('computer')) {
    return 'Desktop Computer';
  }
  if (deviceLower.includes('laptop')) {
    return 'Laptop';
  }
  if (deviceLower.includes('tv') || deviceLower.includes('television')) {
    return 'Smart TV';
  }
  if (deviceLower.includes('watch')) {
    return 'Smartwatch';
  }
  if (deviceLower.includes('console') || deviceLower.includes('playstation') || deviceLower.includes('xbox')) {
    return 'Gaming Console';
  }
  
  // Fallback: capitalize first letter
  if (deviceType && deviceType !== 'unknown') {
    return deviceType.charAt(0).toUpperCase() + deviceType.slice(1);
  }
  
  return null;
};

/**
 * Get friendly IP address display (convert loopback addresses to "localhost")
 * @param {string} ip - IP address
 * @returns {string} Friendly IP address or "localhost" for loopback addresses
 */
export const getFriendlyIPAddress = (ip) => {
  if (!ip) return ip;
  
  // Convert various loopback addresses to "localhost"
  const loopbackAddresses = [
    '127.0.0.1',      // IPv4 loopback
    '::1',            // IPv6 loopback
    '::ffff:127.0.0.1', // IPv4-mapped IPv6 loopback
    '0:0:0:0:0:0:0:1', // IPv6 loopback (full notation)
  ];
  
  if (loopbackAddresses.includes(ip)) {
    return 'localhost';
  }
  
  return ip;
};

/**
 * Get a short location string from location data
 * @param {object} location - Location object with city, country, etc.
 * @returns {string|null} Friendly location string or null if unknown
 */
export const getFriendlyLocation = (location) => {
  if (!location) return null;
  
  const { city, country } = location;
  
  if (city && country) {
    return `${city}, ${country}`;
  }
  
  if (country) {
    return country;
  }
  
  if (city) {
    return city;
  }
  
  return null;
};

/**
 * Format relative time for session activity using moment
 * @param {string|Date} date - Date string or Date object
 * @param {string} locale - Locale to use for formatting (e.g., 'en', 'es')
 * @returns {string|null} Relative time string or null if unknown
 * @deprecated Use moment().locale(locale).fromNow() directly in components instead
 */
export const formatRelativeTime = (date, locale = 'en') => {
  if (!date) return null;
  
  // This function is deprecated - use moment().locale(locale).fromNow() directly in components
  // Keeping for backward compatibility but components should import moment directly
  const moment = require('moment');
  return moment(date).locale(locale).fromNow();
};
